import { useState } from "react";
import { ClickAwayListener } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { QuoteSupplierItemPartResponse } from "@/main/models/response";
import QuoteProcurementPartInfo, {
	RenderQuoteItemInformation,
	getTooltipFormatted,
} from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import { renderPartInfoRow } from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfoRow";
import { sanitizePartInfoText } from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfoUtils";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import useWindowDimensions from "@/main/hooks/useKogWindowDimension";
import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";
import colors from "@/main/style/colors";
import * as S from "../../styles";

interface ICellPartNameProps {
	row: QuoteSupplierItemPartResponse;
	rowSpan?: number;
	verticalAlign?: "top" | "middle" | "bottom";
}

function IconNotes({ row }: { row: QuoteSupplierItemPartResponse }) {
	const [isOpenTooltipNotes, setIsOpenTooltipNotes] = useState(false);
	const { width } = useWindowDimensions();

	return (
		<ClickAwayListener onClickAway={() => setIsOpenTooltipNotes(false)}>
			<S.AreaIconPartInfo title={getTooltipFormatted(row.quoteProcurementPartInfo)}>
				<TooltipComponent
					open={isOpenTooltipNotes}
					title={
						<QuoteProcurementPartInfo
							isEllipsisText={false}
							data={row.quoteProcurementPartInfo}
							renderType="column"
							showLineSupplierInfo={{ info: "all" }}
						/>
					}
					placement="top"
					onClose={() => setIsOpenTooltipNotes(false)}
					disableFocusListener
					disableHoverListener
					disableTouchListener
				>
					<S.ButtonIconPartInfo
						id="id-quoteProcurementPartInfoColumn-iconButton"
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							if (width < ResolutionsEnum.desktop) {
								setIsOpenTooltipNotes((prev) => !prev);
							}
						}}
					>
						<WarningIcon style={{ color: colors.orange }} fontSize="small" />
					</S.ButtonIconPartInfo>
				</TooltipComponent>
			</S.AreaIconPartInfo>
		</ClickAwayListener>
	);
}

export default function ColumnPartName({ row, rowSpan, verticalAlign }: ICellPartNameProps) {
	const tireInfo = sanitizePartInfoText(row.quoteProcurementPartInfo?.tireInfo);
	const glassInfo = sanitizePartInfoText(row.quoteProcurementPartInfo?.glassInfo);

	const showIconPartTitle = row.quoteProcurementPartInfo?.notes || tireInfo || glassInfo;

	return (
		<S.TableCellGrid
			align="left"
			$showMobile
			{...(rowSpan ? { rowSpan } : {})}
			style={verticalAlign ? { verticalAlign } : undefined}
		>
			<S.AreaCollumnPart>
				<S.AreaPartName>
					<S.QuoteProcurementPartInfoRowTooltip title={getTooltipFormatted(row.quoteProcurementPartInfo)}>
						<RenderQuoteItemInformation quoteItemDescription={row.quoteProcurementPartInfo?.quoteItemDescription} />
						{renderPartInfoRow(row.quoteProcurementPartInfo)}
						{row.quoteProcurementPartInfo?.notes ? ` [${row.quoteProcurementPartInfo?.notes}]` : ""}
					</S.QuoteProcurementPartInfoRowTooltip>
					{showIconPartTitle && <IconNotes row={row} />}
				</S.AreaPartName>
				{tireInfo && <p title={tireInfo}>{tireInfo}</p>}
				{glassInfo && <p title={glassInfo}>{glassInfo}</p>}
			</S.AreaCollumnPart>
		</S.TableCellGrid>
	);
}
